import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { apiGet } from '../../services/_common';
import { bScreenMode, IUser } from '../../atoms';
import { useThemeLogo } from '../../services/getInitialColorMode';
import { PulseLoader } from 'react-spinners';
import Itlog from '../../assets/images/logo/itlog.png';
import IssueGuide from '../IssueGuide';
import { ERROR } from '../../_constants';

interface ISafeBoardT {
  sbdAction: string;
  sbdFontcolor: string;
  sbdName: string;
}

const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지

// 안전 중점사항
export const SafetyMainPoint = ({ userInfo, type, refetchInterval }: { userInfo: IUser; type: 's' | 'c'; refetchInterval: number }) => {
  const colorMode = useRecoilValue(bScreenMode);
  const { hCd, sCd } = userInfo;

  const contentsReqInfo = type === 's' ? { path: '/safe/boardcontents', reqParam: { hCd, sCd } } : { path: '/safe/cboardcontents', reqParam: { cCd: hCd } };
  const normalReqInfo = type === 's' ? { path: '/head/info', reqParam: { hCd } } : { path: '/company/info/one', reqParam: { cCd: hCd } };

  const {
    data: siteNoteBoardData,
    isLoading,
    isError,
  } = useQuery(['siteSafetyMainPointBoardGet', userInfo], () => fetchData(), {
    enabled: type === 's' ? !!hCd && !!sCd && sCd !== '00000' : !!hCd,
    ...(refetchInterval !== undefined && { refetchInterval }), // 30분
  });

  const fetchData = async () => {
    try {
      const res = await apiGet({ path: contentsReqInfo.path, req: contentsReqInfo.reqParam });
      if (res.status === 200) {
        const data = type === 's' ? res.data.data.safeBoardTList : res.data.data;
        const marqueeContent = data.map((el: ISafeBoardT, index: number) => (
          <span key={index} className={el.sbdAction === 'Y' ? 'blink' : ''} style={{ color: el.sbdFontcolor }}>
            {el.sbdName} &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
          </span>
        ));

        const totalLengthWithoutSpaces = data.map((el: ISafeBoardT) => el.sbdName.replace(/\s+/g, '')).reduce((sum: number, sbdNameWithoutSpaces: string) => sum + sbdNameWithoutSpaces.length, 0);

        const getFlowTime = () => {
          if (totalLengthWithoutSpaces < 10) return totalLengthWithoutSpaces * 1.5;
          if (totalLengthWithoutSpaces < 50) return totalLengthWithoutSpaces;
          if (totalLengthWithoutSpaces < 100) return totalLengthWithoutSpaces / 3;
          if (totalLengthWithoutSpaces < 200) return totalLengthWithoutSpaces / 2.5;
          return totalLengthWithoutSpaces / 2.5;
        };

        return { combinedText: marqueeContent, flowTime: getFlowTime() };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const headInfoQuery = useQuery(['headInfoGet', hCd], () => apiGet({ path: normalReqInfo.path, req: normalReqInfo.reqParam }), {
    retry: 3,
  });
  const theme = colorMode === '1' ? 'light' : 'dark';
  const logo = useThemeLogo(headInfoQuery, theme, type === 's' ? 'h' : 'c');

  const loading = isLoading || !siteNoteBoardData;

  if (isError) return <IssueGuide text={ERROR} isImg={false} />;
  if (loading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <div className='safetyMainPoint'>
      <div className='content' style={{ fontSize: '3rem', display: 'flex' }}>
        <div className='logoWrapper'>
          <img src={logo !== '' ? `${logo}?v=${randomString}` : Itlog} alt='' />
        </div>
        <div className='marquee'>
          <div className='marquee-content' style={{ animation: `marquee ${siteNoteBoardData?.flowTime ?? 0}s linear infinite` }}>
            {siteNoteBoardData?.combinedText ?? ''}
          </div>
        </div>
      </div>
    </div>
  );
};
